import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';

const PROVIDER_LABELS = {
  claude: 'Claude (Anthropic)',
  openai: 'ChatGPT (OpenAI)',
};

export default function AIChat() {
  const [status, setStatus] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [activeProvider, setActiveProvider] = useState('claude');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.getAIStatus();
      setStatus(data);
      setActiveProvider(data.preferredProvider || 'claude');
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const hasAnyKey = status?.claudeConfigured || status?.openaiConfigured;

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMessage = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setSending(true);
    setError('');

    try {
      const data = await api.sendAIMessage(activeProvider, userMessage.content, messages);
      setMessages([...newMessages, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setError(err.message);
      setMessages(newMessages); // keep the user's message, drop the failed attempt
    } finally {
      setSending(false);
    }
  };

  if (!status) {
    return <div style={styles.panel}><p style={styles.empty}>Loading...</p></div>;
  }

  return (
    <div style={styles.wrap}>
      {(!hasAnyKey || showSettings) && (
        <AISettings
          status={status}
          onUpdated={loadStatus}
          onClose={hasAnyKey ? () => setShowSettings(false) : null}
        />
      )}

      {hasAnyKey && !showSettings && (
        <div style={styles.chatPanel}>
          <div style={styles.header}>
            <h2 style={styles.title}>🤖 Ask AI</h2>
            <div style={styles.headerRight}>
              {status.claudeConfigured && status.openaiConfigured && (
                <select
                  style={styles.providerSelect}
                  value={activeProvider}
                  onChange={(e) => setActiveProvider(e.target.value)}
                >
                  <option value="claude">Claude</option>
                  <option value="openai">ChatGPT</option>
                </select>
              )}
              <button style={styles.settingsBtn} onClick={() => setShowSettings(true)}>
                ⚙ Settings
              </button>
            </div>
          </div>

          <p style={styles.disclaimer}>
            A summary of your budget, top spending categories, subscriptions,
            and net worth is sent to {PROVIDER_LABELS[activeProvider]} to
            answer your question. Your raw transaction list is never sent -
            just the totals. Chat isn't saved and resets when you leave this
            tab.
          </p>

          <div style={styles.messages} ref={scrollRef}>
            {messages.length === 0 ? (
              <p style={styles.emptyChat}>
                Ask something like "Am I on track this month?" or "What's my biggest subscription?"
              </p>
            ) : (
              messages.map((m, i) => (
                <div key={i} style={m.role === 'user' ? styles.userBubbleRow : styles.aiBubbleRow}>
                  <div style={m.role === 'user' ? styles.userBubble : styles.aiBubble}>
                    {m.content}
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div style={styles.aiBubbleRow}>
                <div style={styles.aiBubble}>...</div>
              </div>
            )}
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <form onSubmit={sendMessage} style={styles.inputRow}>
            <input
              style={styles.input}
              placeholder="Ask about your budget..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={sending}
            />
            <button style={styles.sendBtn} type="submit" disabled={sending || !input.trim()}>
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function AISettings({ status, onUpdated, onClose }) {
  const [claudeKey, setClaudeKey] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');

  const saveKey = async (provider, key) => {
    if (!key.trim()) return;
    setError('');
    setSaving(provider);
    try {
      await api.saveAIKey(provider, key.trim());
      if (provider === 'claude') setClaudeKey('');
      else setOpenaiKey('');
      onUpdated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const removeKey = async (provider) => {
    if (!window.confirm(`Remove your ${PROVIDER_LABELS[provider]} API key?`)) return;
    try {
      await api.deleteAIKey(provider);
      onUpdated();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h2 style={styles.title}>🔑 Connect Your AI</h2>
        {onClose && (
          <button style={styles.settingsBtn} onClick={onClose}>Done</button>
        )}
      </div>
      <p style={styles.helpText}>
        Bring your own API key - you use your own Claude or ChatGPT account,
        and you pay that provider directly for usage. Boring Budgeting never
        sees your key in plain text; it's encrypted before it's stored.
      </p>

      <ProviderRow
        label="Claude (Anthropic)"
        configured={status.claudeConfigured}
        keyValue={claudeKey}
        onKeyChange={setClaudeKey}
        onSave={() => saveKey('claude', claudeKey)}
        onRemove={() => removeKey('claude')}
        saving={saving === 'claude'}
        helpUrl="https://console.anthropic.com/settings/keys"
      />

      <ProviderRow
        label="ChatGPT (OpenAI)"
        configured={status.openaiConfigured}
        keyValue={openaiKey}
        onKeyChange={setOpenaiKey}
        onSave={() => saveKey('openai', openaiKey)}
        onRemove={() => removeKey('openai')}
        saving={saving === 'openai'}
        helpUrl="https://platform.openai.com/api-keys"
      />

      {error && <p style={styles.error}>{error}</p>}
    </div>
  );
}

function ProviderRow({ label, configured, keyValue, onKeyChange, onSave, onRemove, saving, helpUrl }) {
  return (
    <div style={styles.providerRow}>
      <div style={styles.providerHeader}>
        <span style={styles.providerLabel}>{label}</span>
        {configured && <span style={styles.configuredBadge}>✓ Connected</span>}
      </div>
      {configured ? (
        <button style={styles.removeBtn} onClick={onRemove}>Remove key</button>
      ) : (
        <div style={styles.keyForm}>
          <input
            style={styles.keyInput}
            type="password"
            placeholder="Paste your API key"
            value={keyValue}
            onChange={(e) => onKeyChange(e.target.value)}
          />
          <button style={styles.saveBtn} onClick={onSave} disabled={saving || !keyValue.trim()}>
            {saving ? '...' : 'Save'}
          </button>
        </div>
      )}
      <a href={helpUrl} target="_blank" rel="noopener noreferrer" style={styles.helpLink}>
        Get a key from {label} →
      </a>
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  chatPanel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    height: '70vh',
    minHeight: '400px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  headerRight: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  providerSelect: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '4px 8px',
    color: '#fff',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
  },
  settingsBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  disclaimer: {
    color: '#4b5563',
    fontSize: '0.7rem',
    lineHeight: '1.4',
    marginBottom: '1rem',
  },
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1.25rem',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
    marginBottom: '0.75rem',
  },
  emptyChat: {
    color: '#4b5563',
    fontSize: '0.85rem',
    textAlign: 'center',
    margin: 'auto',
  },
  userBubbleRow: { display: 'flex', justifyContent: 'flex-end' },
  aiBubbleRow: { display: 'flex', justifyContent: 'flex-start' },
  userBubble: {
    background: '#00d4aa',
    color: '#0a0f1e',
    borderRadius: '12px 12px 2px 12px',
    padding: '0.6rem 0.9rem',
    fontSize: '0.85rem',
    maxWidth: '75%',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'pre-wrap',
  },
  aiBubble: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    color: '#e5e7eb',
    borderRadius: '12px 12px 12px 2px',
    padding: '0.6rem 0.9rem',
    fontSize: '0.85rem',
    maxWidth: '75%',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'pre-wrap',
  },
  error: { color: '#ef4444', fontSize: '0.75rem', margin: '0 0 0.5rem' },
  inputRow: { display: 'flex', gap: '0.5rem' },
  input: {
    flex: 1,
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.7rem 0.9rem',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
  },
  sendBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 1.2rem',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center' },
  providerRow: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '1rem',
    marginBottom: '0.75rem',
  },
  providerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.6rem',
  },
  providerLabel: {
    color: '#e5e7eb',
    fontSize: '0.85rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  configuredBadge: {
    color: '#00d4aa',
    fontSize: '0.7rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
  },
  keyForm: { display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' },
  keyInput: {
    flex: 1,
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '0.5rem 0.75rem',
    color: '#fff',
    fontSize: '0.8rem',
    fontFamily: "'DM Mono', monospace",
  },
  saveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '0.5rem 1rem',
    fontSize: '0.75rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  removeBtn: {
    background: 'transparent',
    border: '1px solid #ef4444',
    color: '#ef4444',
    borderRadius: '6px',
    padding: '5px 12px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    marginBottom: '0.5rem',
  },
  helpLink: {
    color: '#00d4aa',
    fontSize: '0.7rem',
    textDecoration: 'none',
  },
};
